import { keepLatestTask, task, timeout } from 'ember-concurrency';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import User, { UserResourceName } from 'volta/models/user';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import type SessionService from 'ember-simple-auth/services/session';
import type Owner from '@ember/owner';
import type UserPreferences from 'volta/models/user-preference';
import type StoreService from 'volta/services/store';
import type { TError } from 'volta/utils/error-utils';
import type {
  IDashboardWidget,
  IMenuPreferences,
  IPagesPreferences,
  IUpdatePreferencesCmd
} from 'volta/models/types/user';
import type { TEntityType } from 'volta/models/types/entity-types';
import type EventStreamService from './event-stream';

export default class SessionUserService extends Service {
  @service session!: SessionService;
  @service store!: StoreService;
  @service eventStream!: EventStreamService;

  /**
   * Session user model loaded from the store by the reloadUser async function
   */
  @tracked
  declare user: User;

  @tracked
  declare preferences: UserPreferences;

  constructor(owner: Owner) {
    super(owner);
    this.eventStream.addHandler(
      UserResourceName.plural,
      ['UserDeactivated'],
      this.handleDeactivatedUserEvent,
      this
    );
  }

  async handleDeactivatedUserEvent(payload: any) {
    const user = normalize('user', payload) as User;
    if (user.id === this.userId) {
      await this.session.invalidate();
    }
  }

  /**
   * Alias getter for the session user id
   */
  get userId() {
    return this.session.data.authenticated['userId'] as string;
  }

  get isSupplierUser() {
    return Boolean(this.user?.isSupplier);
  }

  get suppliers() {
    return this.user?.suppliers.map((s) => s.id!);
  }

  initPreferences = task(async () => {
    try {
      this.preferences = await this.store.findRecord<UserPreferences>(
        'user-preference',
        this.user.id!
      );
      this.user.preferences = this.preferences;
      this.preferences.user = this.user;
      return this.preferences;
    } catch (error) {
      defaultErrorHandler(error as TError);
      throw error;
    }
  });

  setPreferences(cmd: IUpdatePreferencesCmd) {
    if (cmd.theme) {
      this.preferences.theme = cmd.theme;
    }
    if (cmd.locale) {
      this.preferences.locale = cmd.locale;
    }
    if (cmd.avatar) {
      this.preferences.avatar = cmd.avatar;
    }
    if (cmd.timeZone) {
      this.preferences.timeZone = cmd.timeZone;
    }
    this._updatePreferences.perform();
  }

  setDashboard(dashboard: IDashboardWidget[]) {
    this.preferences.dashboard = dashboard;
    this._updateDashboard.perform();
  }

  setMenuProperty(key: keyof IMenuPreferences, prop: unknown) {
    const { menu } = this.preferences;
    this.preferences.menu = { ...menu, [key]: prop };
    this._updateInterface.perform();
  }

  async setPageProperty<T = unknown>(key: string, pref: IPagesPreferences<T>) {
    const { pages = {} } = this.preferences;
    this.preferences.pages = { ...pages, [key]: pref };
    return this._updateInterface.perform();
  }

  async setCollectionViewsOrder(entityType: TEntityType, collectionViewIds: string[]) {
    const { collectionViewsOrder = {} } = this.preferences;
    this.preferences.collectionViewsOrder = {
      ...collectionViewsOrder,
      [entityType]: collectionViewIds
    };
    return this._updateInterface.perform();
  }

  /**
   * Reload the session user model from the store
   */
  async reloadUser() {
    if (!this.userId) {
      return;
    }

    if (!this.user) {
      const adapterOptions = {
        query: {
          include: 'roles,warehouses,suppliers,preferences,workshops'
        }
      };

      this.user = await this.store.findRecord<User>('user', this.userId, { adapterOptions });
      await this.initPreferences.perform();
    }

    return this.user;
  }

  _updateInterface = keepLatestTask(async () => {
    try {
      const { menu, pages, collectionViewsOrder } = this.preferences;

      // Avoid rerender on other preferences (i.e dashboard)
      const updatedPreferences = await this.preferences.updateInterface({
        menu,
        pages,
        collectionViewsOrder
      });
      this.preferences.menu = { ...updatedPreferences.menu };
      this.preferences.pages = { ...updatedPreferences.pages };
      this.preferences.collectionViewsOrder = { ...updatedPreferences.collectionViewsOrder };
      await timeout(2000);
      return this.preferences;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  _updateDashboard = keepLatestTask(async () => {
    try {
      const { dashboard } = this.preferences;
      this.preferences = await this.preferences.updateDashboard({ dashboard });
      await timeout(2000);
      return this.preferences;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  _updatePreferences = keepLatestTask(async () => {
    try {
      const cmd = this.preferences.profilePrefs;
      this.preferences = await this.preferences.updatePreferences(cmd);
      await timeout(2000);
      return this.preferences;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });
}

declare module '@ember/service' {
  interface Registry {
    'session-user': SessionUserService;
  }
}
